import { Extension, type Editor } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/** A confirmed highlight: a comment anchored to a `[from, to]` range. */
export interface CommentHighlightRange {
  commentId: string;
  from: number;
  to: number;
}

export interface CommentHighlightOptions {
  /** Called with EVERY comment id whose highlight covers the clicked position (for cycling). */
  onCommentClick: (commentIds: string[]) => void;
}

interface HighlightPluginState {
  highlights: CommentHighlightRange[];
  draft: { from: number; to: number } | null;
  focusedId: string | null;
}

type HighlightMeta =
  | { type: 'set'; highlights: CommentHighlightRange[] }
  | { type: 'upsert'; commentId: string; from: number; to: number }
  | { type: 'remove'; commentId: string }
  | { type: 'draft'; range: { from: number; to: number } | null }
  | { type: 'confirm'; commentId: string }
  | { type: 'focus'; commentId: string | null };

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    commentHighlight: {
      /** Replace all confirmed highlights (hydration from comment annotations). */
      setCommentHighlights: (highlights: CommentHighlightRange[]) => ReturnType;
      /** Add or replace one confirmed highlight (e.g. restore on edit-cancel). */
      upsertCommentHighlight: (highlight: CommentHighlightRange) => ReturnType;
      /** Remove a comment's highlight. */
      removeCommentHighlight: (commentId: string) => ReturnType;
      /** Set or clear the in-progress (pending) draft selection. */
      setHighlightDraft: (range: { from: number; to: number } | null) => ReturnType;
      /** Promote the current draft to a confirmed highlight for the given comment. */
      confirmHighlightDraft: (commentId: string) => ReturnType;
      /** Glow the given comment's highlight (or clear focus). */
      setFocusedCommentHighlight: (commentId: string | null) => ReturnType;
    };
  }
}

const COMMENT_HIGHLIGHT_KEY = new PluginKey<HighlightPluginState>('commentHighlight');

/** Current (live, reflowed) confirmed highlight ranges in an editor. */
export function getCommentHighlightRanges(editor: Editor): CommentHighlightRange[] {
  return COMMENT_HIGHLIGHT_KEY.getState(editor.state)?.highlights ?? [];
}

/**
 * Brief text-highlight comments as ProseMirror **decorations** (not marks):
 * highlights are runtime-only, derived from comment annotations, never serialized
 * into content. Overlapping highlights stack natively (nested decoration spans),
 * and ranges reflow with edits by mapping plugin state through each transaction.
 */
export const CommentHighlightExtension = Extension.create<CommentHighlightOptions>({
  name: 'commentHighlight',

  addOptions() {
    return { onCommentClick: () => {} };
  },

  addCommands() {
    const dispatchMeta =
      (meta: HighlightMeta) =>
      ({ tr, dispatch }: { tr: import('@tiptap/pm/state').Transaction; dispatch?: unknown }) => {
        if (dispatch) (dispatch as (t: typeof tr) => void)(tr.setMeta(COMMENT_HIGHLIGHT_KEY, meta));
        return true;
      };

    return {
      setCommentHighlights: (highlights) => dispatchMeta({ type: 'set', highlights }),
      upsertCommentHighlight: ({ commentId, from, to }) =>
        dispatchMeta({ type: 'upsert', commentId, from, to }),
      removeCommentHighlight: (commentId) => dispatchMeta({ type: 'remove', commentId }),
      setHighlightDraft: (range) => dispatchMeta({ type: 'draft', range }),
      confirmHighlightDraft: (commentId) => dispatchMeta({ type: 'confirm', commentId }),
      setFocusedCommentHighlight: (commentId) => dispatchMeta({ type: 'focus', commentId }),
    };
  },

  addProseMirrorPlugins() {
    const { onCommentClick } = this.options;

    return [
      new Plugin<HighlightPluginState>({
        key: COMMENT_HIGHLIGHT_KEY,
        state: {
          init: () => ({ highlights: [], draft: null, focusedId: null }),
          apply(tr, value) {
            let { highlights, draft, focusedId } = value;

            // Reflow ranges with the edit (the mark-replacement that lets
            // highlights follow text as it changes).
            if (tr.docChanged) {
              highlights = highlights.map((h) => ({
                commentId: h.commentId,
                from: tr.mapping.map(h.from),
                to: tr.mapping.map(h.to),
              }));
              draft = draft
                ? { from: tr.mapping.map(draft.from), to: tr.mapping.map(draft.to) }
                : null;
            }

            const meta = tr.getMeta(COMMENT_HIGHLIGHT_KEY) as HighlightMeta | undefined;
            if (meta) {
              switch (meta.type) {
                case 'set':
                  highlights = meta.highlights;
                  break;
                case 'upsert':
                  highlights = [
                    ...highlights.filter((h) => h.commentId !== meta.commentId),
                    { commentId: meta.commentId, from: meta.from, to: meta.to },
                  ];
                  break;
                case 'remove':
                  highlights = highlights.filter((h) => h.commentId !== meta.commentId);
                  break;
                case 'draft':
                  draft = meta.range;
                  break;
                case 'confirm':
                  if (draft) {
                    highlights = [
                      ...highlights.filter((h) => h.commentId !== meta.commentId),
                      { commentId: meta.commentId, from: draft.from, to: draft.to },
                    ];
                    draft = null;
                  }
                  break;
                case 'focus':
                  focusedId = meta.commentId;
                  break;
              }
            }

            return { highlights, draft, focusedId };
          },
        },
        props: {
          decorations(state) {
            const pluginState = COMMENT_HIGHLIGHT_KEY.getState(state);
            if (!pluginState) return null;
            const { highlights, draft, focusedId } = pluginState;
            const decorations: Decoration[] = [];

            for (const h of highlights) {
              // Collapsed range = its text was deleted; render nothing (the save
              // flow flags it `detached` / unanchored).
              if (h.from >= h.to) continue;
              decorations.push(
                Decoration.inline(h.from, h.to, {
                  class:
                    h.commentId === focusedId
                      ? 'comment-highlight comment-highlight--focused'
                      : 'comment-highlight',
                  'data-comment-id': h.commentId,
                  tabindex: '0',
                  // eslint-disable-next-line i18next/no-literal-string -- ARIA role on the decoration
                  role: 'button',
                }),
              );
            }

            if (draft && draft.from < draft.to) {
              decorations.push(
                Decoration.inline(draft.from, draft.to, {
                  class: 'comment-highlight comment-highlight--pending',
                }),
              );
            }

            return DecorationSet.create(state.doc, decorations);
          },
          handleClick(view, pos) {
            const pluginState = COMMENT_HIGHLIGHT_KEY.getState(view.state);
            if (!pluginState) return false;
            const ids = pluginState.highlights
              .filter((h) => h.from < h.to && pos >= h.from && pos < h.to)
              .map((h) => h.commentId);
            if (ids.length === 0) return false;
            onCommentClick(ids);
            // Let ProseMirror place the cursor; focus styling follows separately.
            return false;
          },
          handleDOMEvents: {
            keydown(_view, event) {
              if (event.key !== 'Enter' && event.key !== ' ') return false;
              const target = event.target as HTMLElement;
              const el = target.closest('[data-comment-id]');
              const commentId = el?.getAttribute('data-comment-id');
              if (!commentId) return false;
              event.preventDefault();
              onCommentClick([commentId]);
              return true;
            },
          },
        },
      }),
    ];
  },
});
